import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Megaphone, MailCheck, ShieldCheck, Ban } from "lucide-react";
import api from "@/lib/api";

/**
 * Aba Campanhas: acompanhamento da Black Friday 2026 (leads identificados),
 * prévia do e-mail de abertura (SOMENTE visualização — nenhum disparo sem
 * autorização da administradora) e lista de descadastros (LGPD).
 */
export default function AdminCampaigns({ leads }) {
    const [preview, setPreview] = useState(null);
    const [unsubs, setUnsubs] = useState([]);
    const [loading, setLoading] = useState(true);

    const campaignLeads = (leads || []).filter((l) => l.campaign === "black-friday-2026");

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [p, u] = await Promise.all([
                api.get("/api/admin/campaign-email-preview"),
                api.get("/api/admin/unsubscribes"),
            ]);
            setPreview(p.data);
            setUnsubs(u.data);
        } catch {
            toast.error("Não foi possível carregar os dados da campanha.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    if (loading) {
        return (
            <div className="mt-10 flex justify-center" data-testid="campaigns-loading">
                <span className="h-8 w-8 animate-spin rounded-full border-2 border-forest-emerald border-t-transparent" />
            </div>
        );
    }

    return (
        <div className="mt-8 space-y-6" data-testid="admin-campaigns">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <div className="rounded-3xl border border-[#E5DEC9] bg-white p-6 shadow-sm" data-testid="campaign-stat-leads">
                    <span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-gold/15 text-gold-hover">
                        <Megaphone className="h-5 w-5" />
                    </span>
                    <p className="mt-4 font-serif text-3xl font-semibold text-forest-deep">{campaignLeads.length}</p>
                    <p className="mt-1 text-xs font-semibold uppercase tracking-[0.12em] text-stone-500">Interessados Black Friday 2026</p>
                </div>
                <div className="rounded-3xl border border-[#E5DEC9] bg-white p-6 shadow-sm" data-testid="campaign-stat-email">
                    <span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-forest-emerald/10 text-forest-emerald">
                        <MailCheck className="h-5 w-5" />
                    </span>
                    <p className="mt-4 font-serif text-3xl font-semibold text-forest-deep">Modelo pronto</p>
                    <p className="mt-1 text-xs font-semibold uppercase tracking-[0.12em] text-stone-500">E-mail de abertura — sem disparo</p>
                </div>
                <div className="rounded-3xl border border-[#E5DEC9] bg-white p-6 shadow-sm" data-testid="campaign-stat-unsubs">
                    <span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-stone-100 text-stone-500">
                        <Ban className="h-5 w-5" />
                    </span>
                    <p className="mt-4 font-serif text-3xl font-semibold text-forest-deep">{unsubs.length}</p>
                    <p className="mt-1 text-xs font-semibold uppercase tracking-[0.12em] text-stone-500">Descadastros (LGPD)</p>
                </div>
            </div>

            <div className="rounded-3xl border border-[#E5DEC9] bg-white p-6 shadow-sm sm:p-8">
                <h2 className="font-serif text-xl font-semibold text-forest-deep">Prévia do e-mail — abertura da Black Friday</h2>
                <p className="mt-1 flex items-start gap-2 text-xs leading-relaxed text-stone-500">
                    <ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0 text-forest-emerald" />
                    Este é apenas o modelo preparado. Nenhum e-mail será enviado sem sua autorização expressa, e o disparo
                    futuro respeitará o consentimento de cada cliente e a lista de descadastro (LGPD). Nenhum desconto ou
                    benefício não confirmado é anunciado.
                </p>
                {preview && (
                    <>
                        <p className="mt-5 text-[11px] font-semibold uppercase tracking-[0.14em] text-stone-500">Assunto</p>
                        <p className="mt-1.5 rounded-xl bg-sand px-4 py-3 text-sm font-medium text-forest-deep" data-testid="campaign-email-subject">
                            {preview.subject}
                        </p>
                        <p className="mt-5 text-[11px] font-semibold uppercase tracking-[0.14em] text-stone-500">Corpo do e-mail</p>
                        <iframe
                            title="Prévia do e-mail Black Friday"
                            data-testid="campaign-email-preview"
                            srcDoc={preview.html}
                            sandbox=""
                            className="mt-2 h-[520px] w-full rounded-2xl border border-[#E5DEC9] bg-white"
                        />
                    </>
                )}
            </div>

            {unsubs.length > 0 && (
                <div className="rounded-3xl border border-[#E5DEC9] bg-white p-6 shadow-sm sm:p-8" data-testid="campaign-unsubs-list">
                    <h2 className="font-serif text-lg font-semibold text-forest-deep">E-mails descadastrados</h2>
                    <ul className="mt-4 space-y-2">
                        {unsubs.map((u) => (
                            <li key={u.email} className="flex items-center justify-between rounded-xl bg-sand px-4 py-2.5 text-sm text-stone-600">
                                <span>{u.email}</span>
                                <span className="text-[11px] text-stone-400">{new Date(u.unsubscribed_at).toLocaleDateString("pt-BR")}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
}
